import React, { useEffect } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { Flex } from "@chakra-ui/react";

import { applySmoothwallTheme } from "@/js/utils/applySmoothwallTheme";

import { LoginRouteWrapper } from "@/js/components/Root/LoginRouteWrapper";
import Login from "@/js/components/Login";
import DeviceLogin from "@/js/components/Login/DeviceLogin";
import LoginAzureCallback from "@/js/components/Login/LoginAzureCallback";
import LoginClasslinkCallback from "@/js/components/Login/LoginClasslinkCallback";
import LoginGoogleCallback from "@/js/components/Login/LoginGoogleCallback";
import LoginMasqueradeCallback from "@/js/components/Login/LoginMasqueradeCallback";
import AutoMagicLogin from "@/js/components/AutoMagicLogin";

import Classrooms from "@/js/components/Classrooms";
import { AuthenticatedRouteWrapper } from "@/js/components/Root/AuthenticatedRouteWrapper";
import ClassroomWindowsV2 from "@/js/components/ClassroomWindowsV2";
import Courses from "@/js/components/Courses";
import HallwayPass from "@/js/components/HallwayPass";

import { useThemeStore } from "@/stores/v2/themeStore";
import { useCoursesStore } from "@/stores/v2/coursesStore";
import { useLicenseStore } from "@/stores/v2/licenseStore";
import { usePosthogRouteChangeListener } from "@/utils/posthogUtils";

// The hall pass license is fetched after this tree mounts. The route has to exist on the
// first render, otherwise a reload of /hallway-pass matches nothing and the page stays blank.
export function HallwayPassRoute(): JSX.Element {
	const hallPassEnabled = useLicenseStore((state) => state.hallPass);
	const { classId } = useParams();

	if (!hallPassEnabled) {
		return <Navigate to={classId ? `/classroom/${classId}` : "/classrooms/overview"} replace />;
	}

	return <HallwayPass />;
}

export default function Root(): JSX.Element {
	const themeStore = useThemeStore();
	const coursesStore = useCoursesStore();

	// Apply Smoothwall theme if required
	useEffect(() => {
		// themeStore is used as the source of truth for what theme needs to be displayed.
		// This value comes from `customer_internal_company` from /deviceinfo
		if (themeStore.theme === "smoothwall") applySmoothwallTheme();

		// If smoothwall is present in URL this will override the value in themeStore and apply the Smoothwall theme.
		// This is required for the login screen, because `customer_internal_company` from /deviceinfo is only available when logged in.
		if (window.location.host.includes("smoothwall")) applySmoothwallTheme();
	}, [themeStore]);

	usePosthogRouteChangeListener();

	return (
		<Flex minHeight="100vh" flexDirection="column" bg="var(--background-gradient)" data-testid="root-component">
			<Routes>
				{/* Unauthenticated Routes */}
				<Route path="/login" element={<LoginRouteWrapper />}>
					<Route index element={<Login />} />
					<Route path="google" element={<LoginGoogleCallback />} />
					<Route path="azuread" element={<LoginAzureCallback />} />
					<Route path="classlink" element={<LoginClasslinkCallback />} />
					<Route path="masquerade/callback" element={<LoginMasqueradeCallback />} />
					<Route path=":tenant_key" element={<DeviceLogin />} />
				</Route>

				{/* Authenticated Routes */}
				<Route element={<AuthenticatedRouteWrapper />}>
					<Route path="/classrooms/overview" element={<Classrooms />} />
					<Route path="/classroom/:classId" element={<ClassroomWindowsV2 />} />
					{coursesStore.showCoursesTab && (
						// Points all '/courses' and '/courses/*' traffic to the same Courses entry point
						// so the microfrontend can handle it's own routing from there.
						<Route path="/classroom/:classId/courses/*" element={<Courses />} />
					)}
					<Route path="/classroom/:classId/hallway-pass/*" element={<HallwayPassRoute />} />
				</Route>

				{/* Callback URL after appliance has successfully performed auto-magic login */}
				<Route path="/automagiclogin/success" element={<AutoMagicLogin />} />

				{/* Redirects */}
				<Route path="/" element={<Navigate to="/classrooms/overview" replace />} />
				<Route path="/class/screens" element={<Navigate to="/classrooms/overview" replace />} />
				<Route path="/classroom/details" element={<Navigate to="/classrooms/overview" replace />} />
				<Route path="/classrooms" element={<Navigate to="/classrooms/overview" replace />} />
				<Route path="/automagicloginredirect" element={<Navigate to="/login" />} />
			</Routes>
		</Flex>
	);
}
